import React, { useState, useEffect } from 'react';
import styles from './ManagementCompany.module.css';
import { ArrowRight, Save, Building } from 'lucide-react';
import { createManagementCompany, updateManagementCompany, validateManagementCompanyData } from '../../Fetch/fetchManagementCompany';
import ErrorPopup from '../../components/ErrorPopup/ErrorPopup';
import { useApiWithErrorHandling } from '../../hooks/useApiWithErrorHandling';

const ManagementCompanyForm = ({ company, onSave, onCancel, buildingId }) => {
  const [formData, setFormData] = useState({
    Name: '',
    ContactPerson: '',
    Phone: '',
    Email: '',
    Address: '',
    BuildingID: buildingId || null,
    IsActive: true // Add IsActive field with default true
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [errorPopup, setErrorPopup] = useState({
    isOpen: false,
    title: '',
    message: ''
  });

  const { executeWithErrorHandling } = useApiWithErrorHandling();

  // Populate form when editing existing company
  useEffect(() => {
    if (company) {
      setFormData({
        ManagementCompanyID: company.ManagementCompanyID,
        Name: company.Name || '',
        ContactPerson: company.ContactPerson || '',
        Phone: company.Phone || '',
        Email: company.Email || '',
        Address: company.Address || '',
        BuildingID: company.BuildingID || buildingId,
        IsActive: company.IsActive !== undefined ? company.IsActive : true
      });
    } else {
      // Reset form for new company
      setFormData({
        Name: '',
        ContactPerson: '',
        Phone: '',
        Email: '',
        Address: '',
        BuildingID: buildingId || null,
        IsActive: true
      });
    }
  }, [company, buildingId]);

  // Clear errors when form data changes
  useEffect(() => {
    if (Object.keys(fieldErrors).length > 0) {
      setFieldErrors({});
    }
    if (generalError) {
      setGeneralError('');
    }
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;

    // Validate form data
    const validation = validateManagementCompanyData(formData);
    
    if (!validation.isValid) {
      setGeneralError(validation.errors.join(', '));
      return;
    }

    setIsSubmitting(true);
    setGeneralError('');
    setFieldErrors({});

    try {

      let result;
      if (company) {
        // Update existing company
        result = await executeWithErrorHandling(
          () => updateManagementCompany(formData)
        );
      } else {
        // Create new company
        result = await executeWithErrorHandling(
          () => createManagementCompany(formData)
        );
      }


      // Extract the management company data from the API response
      let savedCompanyData;
      
      if (result.managementCompany) {
        // If API returns nested managementCompany object
        savedCompanyData = result.managementCompany;
      } else if (result.ManagementCompanyID || result.managementCompanyID) {
        // If API returns the company data directly
        savedCompanyData = result;
      } else {
        // Fallback: construct from form data + any returned IDs
        savedCompanyData = {
          ...formData,
          ManagementCompanyID: result.ManagementCompanyID || result.managementCompanyID || formData.ManagementCompanyID
        };
      }


      // Call the parent's onSave with the normalized data
      onSave(savedCompanyData);
      
    } catch (error) {
      
      // Handle non-network errors
      if (error.message !== 'NETWORK_ERROR') {
        // Handle specific field errors
        if (error.message.includes('email')) {
          setFieldErrors({ Email: 'כתובת דוא"ל לא תקינה או כבר קיימת במערכת' });
        } else if (error.message.includes('phone')) {
          setFieldErrors({ Phone: 'מספר טלפון לא תקין או כבר קיים במערכת' });
        } else if (error.message.includes('name')) {
          setFieldErrors({ Name: 'שם החברה כבר קיים במערכת' });
        } else if (error.message.includes('Building')) {
          setGeneralError('שגיאה בשדה Building - בדוק שהמודל מעודכן');
        } else {
          setGeneralError(error.message || 'שגיאה בשמירת חברת הניהול');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      Name: '',
      ContactPerson: '',
      Phone: '',
      Email: '',
      Address: '',
      BuildingID: buildingId || null,
      IsActive: true
    });
    setFieldErrors({});
    setGeneralError('');
    onCancel();
  };

  return (
    <div className={styles.companyFormContainer}>
      <div className={styles.formHeader}>
        <button 
          className={styles.backButton} 
          onClick={handleCancel}
          type="button"
        >
          <ArrowRight size={20} />
        </button>
        <h2>
          {company ? 'עריכת חברת ניהול' : 'הוספת חברת ניהול חדשה'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className={styles.companyForm}>
        {generalError && (
          <div className={styles.errorMessage}>
            {generalError}
          </div>
        )}

        <div className={styles.formSection}>
          <h3>פרטי חברת הניהול</h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="Name">
                שם החברה *
              </label>
              <input
                type="text"
                id="Name"
                name="Name"
                value={formData.Name}
                onChange={handleInputChange}
                className={fieldErrors.Name ? styles.error : ''}
                required
                placeholder="הזן שם חברת הניהול"
                disabled={isSubmitting}
              />
              {fieldErrors.Name && (
                <div className={styles.fieldError}>{fieldErrors.Name}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="ContactPerson">
                איש קשר
              </label>
              <input
                type="text"
                id="ContactPerson"
                name="ContactPerson"
                value={formData.ContactPerson}
                onChange={handleInputChange}
                className={fieldErrors.ContactPerson ? styles.error : ''}
                placeholder="הזן שם איש הקשר"
                disabled={isSubmitting}
              />
              {fieldErrors.ContactPerson && (
                <div className={styles.fieldError}>{fieldErrors.ContactPerson}</div>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="Phone">
                טלפון
              </label>
              <input
                type="tel"
                id="Phone"
                name="Phone"
                value={formData.Phone}
                onChange={handleInputChange}
                className={fieldErrors.Phone ? styles.error : ''}
                placeholder="הזן מספר טלפון"
                disabled={isSubmitting}
              />
              {fieldErrors.Phone && (
                <div className={styles.fieldError}>{fieldErrors.Phone}</div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="Email">
                דוא"ל
              </label>
              <input
                type="email"
                id="Email"
                name="Email"
                value={formData.Email}
                onChange={handleInputChange}
                className={fieldErrors.Email ? styles.error : ''}
                placeholder="הזן כתובת דוא״ל"
                disabled={isSubmitting}
              />
              {fieldErrors.Email && (
                <div className={styles.fieldError}>{fieldErrors.Email}</div>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="Address">
                כתובת
              </label>
              <textarea
                id="Address"
                name="Address"
                value={formData.Address}
                onChange={handleInputChange}
                className={fieldErrors.Address ? styles.error : ''}
                placeholder="הזן כתובת חברת הניהול"
                disabled={isSubmitting}
                rows={3}
              />
              {fieldErrors.Address && (
                <div className={styles.fieldError}>{fieldErrors.Address}</div>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="IsActive">
                פעיל
              </label>
              <select
                id="IsActive"
                name="IsActive"
                value={formData.IsActive ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, IsActive: e.target.value === 'true' }))}
                className={fieldErrors.IsActive ? styles.error : ''}
                disabled={isSubmitting}
              >
                <option value="true">פעיל</option>
                <option value="false">לא פעיל</option>
              </select>
              {fieldErrors.IsActive && (
                <div className={styles.fieldError}>{fieldErrors.IsActive}</div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.saveButton}
            disabled={isSubmitting || !formData.Name.trim()}
          >
            <Save size={16} />
            {isSubmitting ? 'שומר...' : (company ? 'עדכן' : 'שמור')}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            ביטול
          </button>
          
        </div>
      </form>
      
      <ErrorPopup
        isOpen={errorPopup.isOpen}
        onClose={() => setErrorPopup({ isOpen: false, title: '', message: '' })}
        title={errorPopup.title}
        message={errorPopup.message}
      />
    </div>
  );
};

export default ManagementCompanyForm;